import {useQueryParams} from 'presentation/pages';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {HealthGuideFavoritePlaceItem} from '../types';
import {getUserInfoFromToken} from 'presentation/utils';
import {useGetFavoritePlacesQuery} from '../queries/useGetFavoritePlacesQuery';
import {useDeleteFavoritePlaceMutation} from '../mutations/useDeleteFavoritePlaceMutation';
import {GuidesEnum, QueryParamsEnum} from 'presentation/constants';
import {useLocationStore} from 'presentation/pages/private/store/locationStore';
import {GuideStep} from 'presentation/modules/guide/shared/constants/constants';
import {getCityLocation} from 'presentation/modules/guide/shared/services/getCityLocation';

export function useListFavoritePlaces() {
	const navigate = useNavigate();
	const {pathname} = useLocation();
	const {setSearchParams} = useQueryParams();
	const {setLocation} = useLocationStore();
	const [anchorContextMenuEl, setAnchorContextMenuEl] = useState<null | HTMLElement>(null);
	const isMenuOpen = Boolean(anchorContextMenuEl);
	const [selectedItem, setSelectedItem] = useState<HealthGuideFavoritePlaceItem | null>(null);
	const [openRemoveFavoritePlaceAlert, setOpenRemoveFavoritePlaceAlert] = useState(false);
	const [openDeleteError, setOpenDeleteError] = useState(false);
	const cdPessoa = getUserInfoFromToken().cdPessoa;
	const [recentlyVisited] = useState<string | null>(() =>
		localStorage.getItem(`favoritePlaceRecentlyVisited-${cdPessoa}`),
	);

	const {data, isLoading} = useGetFavoritePlacesQuery();
	const {
		mutate: deleteFavoritePlace,
		isPending: isDeletingFavoritePlace,
		isSuccess: isDeleteFavoritePlaceSuccess,
		isError: isDeletingFavoritePlaceError,
	} = useDeleteFavoritePlaceMutation();

	const favoritePlaces = data ?? [];

	const recentlyVisitedItem = useMemo(() => {
		if (recentlyVisited === null) return null;
		return favoritePlaces.find(item => item.id === recentlyVisited) ?? null;
	}, [recentlyVisited, favoritePlaces]);

	const listItems = useMemo(() => {
		if (recentlyVisited === null) return favoritePlaces;
		return favoritePlaces.filter(item => item.id !== recentlyVisited);
	}, [recentlyVisited, favoritePlaces]);

	const handleContextMenuOpen = useCallback(
		(event: React.MouseEvent<HTMLElement>, item: HealthGuideFavoritePlaceItem) => {
			event.stopPropagation();
			setAnchorContextMenuEl(event.currentTarget);
			setSelectedItem(item);
		},
		[setAnchorContextMenuEl, setSelectedItem],
	);

	const handleContextMenuClose = useCallback(() => {
		setAnchorContextMenuEl(null);
	}, [setAnchorContextMenuEl]);

	const navToServiceNetworkHealthGuide = useCallback(() => {
		navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);

		setSearchParams(prev => {
			prev.set(QueryParamsEnum.STEP, GuideStep.LOCATION);
			return prev;
		});
	}, [navigate, setSearchParams]);

	const goToDetailsStep = useCallback(
		(item: HealthGuideFavoritePlaceItem, resolvedUfName: string | null) => {
			if (!item?.address) return;
			navigate(GuidesEnum.SERVICE_NETWORK_HEALTH);
			setSearchParams(prev => {
				prev.set(QueryParamsEnum.STEP, GuideStep.DETAILS);
				prev.set(QueryParamsEnum.UF_CODE, item.address.nmUf.toUpperCase());
				prev.set(QueryParamsEnum.UF_NAME, resolvedUfName?.toUpperCase() ?? '');
				prev.set(QueryParamsEnum.CITY_NAME, item.address.nmCidade.toUpperCase());
				prev.set(
					QueryParamsEnum.COORDINATES,
					`${item.address.cdLatitude},${item.address.cdLongitude}`,
				);
				return prev;
			});
		},
		[navigate, setSearchParams],
	);

	const [loadingCardClickId, setLoadingCardClickId] = useState<string | null>(null);

	const setCityLocation = useCallback(
		async (item: HealthGuideFavoritePlaceItem) => {
			try {
				const cityLocationData = await getCityLocation({
					latitude: item.address.cdLatitude,
					longitude: item.address.cdLongitude,
				});
				setLocation(cdPessoa, 'healthGuide', {
					state: {
						cdUf: item.address.nmUf,
						nmUf: cityLocationData?.content?.city.state || item.address.nmUf,
					},
					city: item.address.nmCidade,
				});
				return cityLocationData;
			} catch (error) {
				setLocation(cdPessoa, 'healthGuide', {
					state: {
						cdUf: item.address.nmUf,
						nmUf: item.address.nmUf,
					},
					city: item.address.nmCidade,
				});
				return null;
			}
		},
		[cdPessoa, setLocation],
	);

	const handleCardClick = useCallback(
		async (item: HealthGuideFavoritePlaceItem) => {
			setSelectedItem(item);
			if (!item?.address) return;
			setLoadingCardClickId(item.id);
			try {
				localStorage.setItem(`favoritePlaceRecentlyVisited-${cdPessoa}`, item.id);
				const cityLocationData = await setCityLocation(item);
				const resolvedUfName = cityLocationData?.content?.city.state ?? '';
				goToDetailsStep(item, resolvedUfName);
			} finally {
				setLoadingCardClickId(null);
			}
		},
		[cdPessoa, setCityLocation, goToDetailsStep],
	);

	const handleOpenRemoveFavoritePlaceAlert = () => {
		setOpenRemoveFavoritePlaceAlert(true);
	};

	const handleCloseRemoveFavoritePlaceAlert = () => {
		setOpenRemoveFavoritePlaceAlert(false);
	};

	const handleEdit = useCallback(() => {
		navigate(GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_FAVORITE_PLACES_EDIT, {
			state: {item: selectedItem, from: pathname},
		});
	}, [selectedItem, pathname]);

	const handleAddNewAddress = () => {
		navigate(GuidesEnum.SERVICE_NETWORK_HEALTH_FLOW_FAVORITE_PLACES_ADD, {
			state: {from: pathname},
		});
	};

	const handleDelete = useCallback(() => {
		handleOpenRemoveFavoritePlaceAlert();
	}, [selectedItem]);

	const handleDeleteConfirmed = useCallback(() => {
		if (selectedItem) {
			deleteFavoritePlace({
				cdBeneficiarioEnderecoFavorito: selectedItem.address.cdBeneficiarioEnderecoFavorito,
			});
		}
	}, [selectedItem, deleteFavoritePlace]);

	const handleOpenDeleteError = () => {
		setOpenDeleteError(true);
	};

	const handleCloseDeleteError = () => {
		setOpenDeleteError(false);
	};

	useEffect(() => {
		if (isDeleteFavoritePlaceSuccess) {
			handleCloseRemoveFavoritePlaceAlert();
		}
	}, [isDeleteFavoritePlaceSuccess]);

	useEffect(() => {
		if (isDeletingFavoritePlaceError) {
			handleOpenDeleteError();
		}
	}, [isDeletingFavoritePlaceError]);

	return {
		navToServiceNetworkHealthGuide,
		recentlyVisitedItem,
		listItems,
		handleContextMenuOpen,
		handleContextMenuClose,
		handleCardClick,
		handleEdit,
		handleDelete,
		handleAddNewAddress,
		handleCloseRemoveFavoritePlaceAlert,
		handleDeleteConfirmed,
		handleCloseDeleteError,
		openRemoveFavoritePlaceAlert,
		openDeleteError,
		anchorContextMenuEl,
		isMenuOpen,
		selectedItem,
		isLoading,
		isDeletingFavoritePlace,
		loadingCardClickId,
	};
}
